import { z } from 'zod';
import { AgentStatusSchema } from './agent.types.js';

/**
 * Per-stream usage row in the admin usage snapshot. Joins
 * `AxonEventService.listActiveStreams()` with the corresponding agent row
 * (when one exists) so admins can see which user owns the stream.
 *
 * Agentless streams are typically flow runs. When the flows plugin is
 * installed, `flowName`/`flowRunId` name the in-flight flow run that owns the
 * stream so the monitor can label it with the responsible flow instead of a
 * generic "flow" placeholder. Both are null for agent streams and for
 * agentless streams with no resolvable flow run.
 */
export const UsageStreamSchema = z.object({
  streamId: z.string(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  agentStatus: AgentStatusSchema.nullable(),
  ownerUserId: z.string().nullable(),
  organizationId: z.string(),
  flowName: z.string().nullable(),
  flowRunId: z.string().nullable(),
  subscribers: z.number().int().nonnegative(),
  viewers: z.number().int().nonnegative(),
  eventCount: z.number().int().nonnegative(),
  lastEventAt: z.number().nullable(),
  /**
   * Whether the stream is currently paging its heavy event cache. Replaces
   * the legacy `sseActive` flag — the direct SSE reader was retired in the
   * mid-2026 SDK-bridge cut, so "live read open" is no longer a meaningful
   * per-stream signal. `resident: true` means `backfillStream` has populated
   * the cache and it has not yet been evicted; `false` means the stream is
   * registered (subscribers are still wired) but the heavy event/dedupe
   * state has been paged out.
   */
  resident: z.boolean(),
});
export type UsageStream = z.infer<typeof UsageStreamSchema>;

/**
 * Per-user totals in the admin usage snapshot, bucketed per organization so
 * the same user appears once per org they have activity in. `userId === null`
 * means the row aggregates streams/agents that have no owner (legacy or
 * webhook-triggered work). `organizationId === ''` is the "no organization"
 * bucket (agentless flow streams and anonymous sockets).
 */
export const UsageUserTotalsSchema = z.object({
  userId: z.string().nullable(),
  login: z.string().nullable(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  organizationId: z.string(),
  organizationName: z.string().nullable(),
  organizationSlug: z.string().nullable(),
  /**
   * Teams the user belongs to within this organization. Empty for the
   * anonymous bucket, for users with no team membership, and for the
   * "no organization" bucket.
   */
  teamIds: z.array(z.string()),
  runningAgents: z.number().int().nonnegative(),
  activeStreams: z.number().int().nonnegative(),
  connectedSockets: z.number().int().nonnegative(),
  /**
   * Browser WebSocket viewers this user currently has open on Axon
   * streams in this organization. Counted per subscription, so one user
   * with three stream tabs open counts as three.
   */
  axonViewers: z.number().int().nonnegative(),
});
export type UsageUserTotals = z.infer<typeof UsageUserTotalsSchema>;

/**
 * Per-team totals in the admin usage snapshot, aggregated from the per-user
 * rows of the team's members within the owning organization. A user who
 * belongs to more than one team counts toward each of their teams, so team
 * rows can sum to more than the org total.
 */
export const UsageTeamTotalsSchema = z.object({
  teamId: z.string(),
  organizationId: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  memberCount: z.number().int().nonnegative(),
  runningAgents: z.number().int().nonnegative(),
  activeStreams: z.number().int().nonnegative(),
  connectedSockets: z.number().int().nonnegative(),
  axonViewers: z.number().int().nonnegative(),
});
export type UsageTeamTotals = z.infer<typeof UsageTeamTotalsSchema>;

/**
 * Per-organization totals in the admin usage snapshot. `organizationId === ''`
 * is the "no organization" bucket: agentless streams (typically flow runs,
 * whose owning org is not resolvable from the core snapshot) and anonymous
 * sockets land here. `agentlessStreams` counts the subset of `activeStreams`
 * that have no backing agent row, so admins can tell flow streams apart from
 * agent streams at a glance.
 */
export const UsageOrgTotalsSchema = z.object({
  organizationId: z.string(),
  name: z.string().nullable(),
  slug: z.string().nullable(),
  runningAgents: z.number().int().nonnegative(),
  activeStreams: z.number().int().nonnegative(),
  agentlessStreams: z.number().int().nonnegative(),
  connectedSockets: z.number().int().nonnegative(),
  /**
   * Total in-process Axon subscriber callbacks across the org's streams
   * (browser viewers plus persistent subscribers like the runner status
   * interpreter and plugin listeners).
   */
  axonSubscriptions: z.number().int().nonnegative(),
  /** Browser WebSocket viewers across the org's Axon streams. */
  axonViewers: z.number().int().nonnegative(),
});
export type UsageOrgTotals = z.infer<typeof UsageOrgTotalsSchema>;

export const UsageSnapshotSchema = z.object({
  generatedAt: z.number(),
  /**
   * What the snapshot covers. `'instance'` means it was assembled from one
   * server replica's in-process state (live sockets, resident Axon streams,
   * viewer registries): at N>1 it is that replica's slice of the fleet, not
   * a cross-replica aggregate. A future aggregated view widens this union.
   */
  scope: z.literal('instance'),
  /** Boot-scoped identity of the server replica that built this snapshot. */
  instanceId: z.string(),
  totals: z.object({
    activeStreams: z.number().int().nonnegative(),
    runningAgents: z.number().int().nonnegative(),
    connectedClients: z.number().int().nonnegative(),
  }),
  streams: z.array(UsageStreamSchema),
  perUser: z.array(UsageUserTotalsSchema),
  perTeam: z.array(UsageTeamTotalsSchema),
  perOrg: z.array(UsageOrgTotalsSchema),
});
export type UsageSnapshot = z.infer<typeof UsageSnapshotSchema>;
